// Pieces of the app shell that own their own state, so typing in a form or showing a message re-renders only that piece
// instead of the whole page (App in app.js keeps only session, theme, tab and pages).
(function () {
  const { useState, useEffect, useCallback } = React;

  // JSON request with the session token. Resolves to the parsed body (callers check `success` / `error`), rejects on network errors.
  async function call(token, method, url, body) {
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const isForm = body instanceof FormData;
    if (body && !isForm) headers['Content-Type'] = 'application/json';
    const res = await fetch(url, { method, headers, body: body ? (isForm ? body : JSON.stringify(body)) : undefined });
    return res.json().catch(() => ({}));
  }
  window.PortalApi = { call };

  // Short confirmations ("Section published", "Page moved to trash" + Undo) go through this one Snackbar.
  // notify(message, { actionLabel, onAction }) can be called from any component.
  window.notify = (message, opts = {}) => window.dispatchEvent(new CustomEvent('wb-snackbar', { detail: { message, ...opts } }));

  function AppSnackbar() {
    const { Snackbar } = window.Components;
    const [snack, setSnack] = useState(null); // { message, actionLabel?, onAction?, id }
    useEffect(() => {
      const show = e => setSnack({ ...e.detail, id: Date.now() });
      window.addEventListener('wb-snackbar', show);
      return () => window.removeEventListener('wb-snackbar', show);
    }, []);
    return (
      <Snackbar
        key={snack ? snack.id : 0}
        open={!!snack}
        message={snack ? snack.message : ''}
        actionLabel={snack && snack.actionLabel}
        onAction={() => { const s = snack; setSnack(null); if (s && s.onAction) s.onAction(); }}
        onClose={() => setSnack(null)}
        autoHideDuration={snack && snack.actionLabel ? 8000 : 4000}
      />
    );
  }

  // Server telemetry for admins, polled every 6 s only while a view that shows it is mounted and the browser tab is visible.
  // The last reading is kept between mounts so switching tabs shows numbers at once.
  let lastTelemetry = null;
  function useAdminTelemetry(token, enabled) {
    const [data, setData] = useState(() => lastTelemetry);
    const refresh = useCallback(() => call(token, 'GET', '/api/admin/telemetry')
      .then(d => { if (!d.error) { lastTelemetry = d; setData(d); } })
      .catch(() => {}), [token]);
    useEffect(() => {
      if (!enabled || !token) return;
      const tick = () => { if (document.visibilityState === 'visible') refresh(); };
      tick();
      const timer = setInterval(tick, 6000);
      document.addEventListener('visibilitychange', tick);
      return () => { clearInterval(timer); document.removeEventListener('visibilitychange', tick); };
    }, [enabled, token, refresh]);
    return [data, refresh];
  }

  // Sign-in dialog + the passkey step. Holds the form state; tells App once a session exists.
  // Taken once here (the lazy stand-ins from LazyComponents.js): reading them at render would switch to the real component
  // after extra.js loads, and React would remount the dialog mid-typing.
  const { AuthDialog, PasskeyPrompt } = window.Components;
  function SignInFlow({ open, onClose, onSignedIn }) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [authError, setAuthError] = useState('');
    const [authLoading, setAuthLoading] = useState(false);
    const [mfa, setMfa] = useState(null); // { mfaToken, options } while the passkey step of sign-in is pending
    useEffect(() => { if (open) setAuthError(''); }, [open]);

    const done = data => { setMfa(null); setUsername(''); setPassword(''); onSignedIn(data); };
    const submit = e => {
      e.preventDefault();
      setAuthError('');
      setAuthLoading(true);
      call('', 'POST', '/api/auth/login', { username, password })
        .then(data => {
          setAuthLoading(false);
          if (data.success) done(data);
          else if (data.mfaRequired) { setPassword(''); onClose(); setMfa(data); }
          else setAuthError(data.error || 'Invalid credentials');
        })
        .catch(() => { setAuthLoading(false); setAuthError('Server communication error'); });
    };

    return (
      <>
        <PasskeyPrompt open={!!mfa} mfa={mfa} onDone={done} onCancel={() => { setMfa(null); setAuthError(''); }} />
        <AuthDialog
          open={open}
          onClose={onClose}
          username={username}
          onUsernameChange={setUsername}
          password={password}
          onPasswordChange={setPassword}
          authError={authError}
          authLoading={authLoading}
          onSubmit={submit}
        />
      </>
    );
  }

  // Apps launchpad tab: search and category filter live here; accounts with System health also see live server stats in the welcome card.
  function AppsTab({ user, token, apps, onOpenProfile }) {
    const { AppsView } = window.Components;
    const [search, setSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [telemetry] = useAdminTelemetry(token, window.Components.can(user, 'admin.health'));
    return (
      <AppsView
        user={user}
        apps={apps}
        adminTelemetry={telemetry}
        search={search}
        onSearchChange={setSearch}
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
        onOpenProfile={onOpenProfile}
      />
    );
  }

  window.Components = window.Components || {};
  Object.assign(window.Components, { AppSnackbar, SignInFlow, AppsTab, useAdminTelemetry });
})();
